import { useContext, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import AuthContext from "../../context/auth/AuthContext";
import GuarantorInviteSection, {
  GuarantorInvite,
} from "../../components/GuarantorInviteSection/GuarantorInviteSection";
import { sendUniversalMail } from "../../services/sharedService";
import { toast } from "../../helper/common";
import { Avatar, Badge, Button, Icon } from "../../components/design";
import "./RenewGuarantorsStep.css";

type Props = {
  user: any;
  onBack: () => void;
  onFinish: () => Promise<void> | void;
};

export default function RenewGuarantorsStep({ user, onBack, onFinish }: Props) {
  const { _t } = useTranslator();
  const { user: authUser } = useContext(AuthContext) as any;
  const [invites, setInvites] = useState<GuarantorInvite[]>([]);
  const youName =
    `${user?.fname ?? ""} ${user?.lname ?? ""}`.trim() ||
    authUser?.fname ||
    "You";
  const youEmail = user?.email ?? authUser?.email ?? "";
  const onboardingGuarantors: any[] = (
    user?.tenant_extra_field_id?.selected_onboarding_users ?? []
  ).filter((x: any) => x.type === "guarantor");
  const addedGuarantors: any[] = user?.gurantor_added_id ?? [];

  const [finishing, setFinishing] = useState(false);
  const handleFinish = async () => {
    if (finishing) return;
    setFinishing(true);
    try {
      for (const inv of invites) {
        try {
          await sendUniversalMail({
            receiverEmail: inv.email,
            senderId: localStorage.getItem("saveduserid"),
            emailType: "score",
            status: "processing",
            gurantor_relation: inv.relation,
          });
        } catch {}
      }
      if (invites.length) toast.success(_t("emailSent"));
      await onFinish();
    } finally {
      setFinishing(false);
    }
  };

  return (
    <div className="approvedModal popup-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 wrapper-modal modal-fixed-screen">
      <div className="popup-content2 position-relative popup-content bg-white required-doc-popup renewGuarantors">
        <Button
          variant="ghost"
          size="md"
          className="renewGuarantors__back"
          iconStart={<Icon name="chevronLeft" size={16} />}
          onClick={onBack}
        >
          {_t("goBack", "Back")}
        </Button>

        <h2 className="renewGuarantors__title">
          {_t("yourGuarantors", "Your guarantors")}
        </h2>
        <p className="renewGuarantors__intro">
          {_t(
            "guarantorsStayConnected",
            "Your guarantors stay connected to your profile. Outdated profiles need updating too."
          )}
        </p>

        <div className="renewGuarantors__people">
          <div className="renewGuarantors__person">
            <Avatar name={youName} />
            <div>
              <div className="renewGuarantors__personName">
                <strong>{youName}</strong>
                <Badge tone="neutral">{_t("you", "You")}</Badge>
              </div>
              <div className="renewGuarantors__email">{youEmail}</div>
            </div>
          </div>

          {onboardingGuarantors.map((g: any, i: number) => (
            <div
              key={i}
              className="renewGuarantors__person renewGuarantors__person--indented"
            >
              <Avatar name={g.name} />
              <div className="renewGuarantors__personText">
                <div className="renewGuarantors__personName">
                  <strong>{g.name}</strong>
                  <Badge tone="neutral">{_t("guarantor", "Guarantor")}</Badge>
                </div>
                <div className="renewGuarantors__email">{g.email}</div>
              </div>
            </div>
          ))}
          {addedGuarantors.map((item: any, i: number) => {
            const name =
              `${item?.guarantor_id?.fname ?? ""} ${item?.guarantor_id?.lname ?? ""}`.trim() ||
              "Guarantor";
            const email = item?.guarantor_id?.email ?? "";
            return (
              <div
                key={i}
                className="renewGuarantors__person renewGuarantors__person--indented"
              >
                <Avatar name={name} />
                <div className="renewGuarantors__personText">
                  <div className="renewGuarantors__personName">
                    <strong>{name}</strong>
                    <Badge tone="neutral">{_t("guarantor", "Guarantor")}</Badge>
                  </div>
                  <div className="renewGuarantors__email">{email}</div>
                </div>
              </div>
            );
          })}

          <GuarantorInviteSection
            currentUserEmail={youEmail}
            invites={invites}
            onChange={setInvites}
            _t={_t as any}
          />
        </div>

        <div className="renewGuarantors__footer">
          <span className="renewGuarantors__hint">
            {_t(
              "manageGuarantorsAnytime",
              "You can manage your guarantors anytime from your profile"
            )}
          </span>
          <Button
            variant="primary"
            size="lg"
            onClick={handleFinish}
            loading={finishing}
          >
            {_t("finish", "Finish")}
          </Button>
        </div>
      </div>
    </div>
  );
}
